import { Box, Button, Flex, Text } from '@chakra-ui/react';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  ResolvedLoanFragmentGQLType,
  InvoiceItemInputGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { useCreateInvoiceContext } from '../../hooks/useCreateInvoice';
import { AccrualsByTAccountMap } from '../utils';
import {
  CreateInvoiceHeaderWithTimelineStepper,
  CreateInvoiceStepIndex,
} from './CreateInvoiceHeaderWithTimelineStepper';
import { CreateInvoiceLineItemsCards } from './CreateInvoiceLineItemsCards';
import { CreateInvoiceLineItemsTable } from './CreateInvoiceLineItemsTable';
import { FullDateRangePicker } from 'packages/common-frontend/components/DateRangePicker';
import { InfoAlertWithDescription } from 'packages/common-frontend/components/InfoAlertWithDescription';
import { useGetCurrentLiabilityAccountsAndAccrualsForLoanQuery } from 'packages/loans/requests/loans.gql.generated';

const CreateLoanInvoiceLineItems = ({
  setCurrentStepIndex,
  loan,
  invoiceComponentIndex,
}: {
  setCurrentStepIndex: React.Dispatch<
    React.SetStateAction<CreateInvoiceStepIndex>
  >;
  loan: ResolvedLoanFragmentGQLType;
  invoiceComponentIndex: number;
}) => {
  const {
    invoice,
    invoiceComponentsWithInvoiceItems,
    updateInvoice: originalUpdateInvoice,
    updateInvoiceComponent: originalUpdateInvoiceComponent,
    updateInvoiceItemsAndTotalsForInvoiceComponent: originalUpdateInvoiceItemsAndTotalsForInvoiceComponent,
  } = useCreateInvoiceContext();

  const {
    data: accrualsData,
    loading: isAccrualsLoading,
  } = useGetCurrentLiabilityAccountsAndAccrualsForLoanQuery({
    variables: {
      accrualStartDate: invoice.accrualStartDate,
      accrualEndDate: invoice.accrualEndDate,
      loanId: loan.id,
    },
  });

  const invoiceItems =
    invoiceComponentsWithInvoiceItems[invoiceComponentIndex].invoiceItems;

  // store the updaters in refs to avoid re-renders from the useEffect below
  const updateInvoiceRef = useRef(originalUpdateInvoice);
  const updateInvoiceComponentRef = useRef(originalUpdateInvoiceComponent);
  const updateInvoiceItemsAndTotalsForInvoiceComponentRef = useRef(
    originalUpdateInvoiceItemsAndTotalsForInvoiceComponent,
  );

  const areAllComponentsFundedOnLoan = useMemo(
    () =>
      loan.funding.requests.every((request) =>
        request.fundingComponents.every(
          (component) => component.fundTransferDate,
        ),
      ),
    [loan.funding.requests],
  );

  const totalAccruedAndProjectedOutstandingBalanceAmount = useMemo(() => {
    return (accrualsData?.accounts ?? []).reduce<number>((acc, account) => {
      if (
        account.tAccount.tAccount === 'adminAgentPrincipalPayable' ||
        account.tAccount.tAccount === 'longTermDebt'
      ) {
        // eslint-disable-next-line no-param-reassign
        acc += account.totalAccruedAmount;
      }

      return acc;
    }, 0);
  }, [accrualsData]);

  useEffect(() => {
    // Invoice items are already set
    if (invoiceItems.length > 0) {
      return;
    }

    // Accrual data is not available yet or there is none for the selected date range
    if (
      isAccrualsLoading ||
      !accrualsData ||
      accrualsData.accounts.length === 0
    ) {
      updateInvoiceRef.current({ field: 'totalAmount', value: 0 });
      updateInvoiceComponentRef.current({
        invoiceComponentIndex,
        field: 'totalAmount',
        value: 0,
      });
      updateInvoiceItemsAndTotalsForInvoiceComponentRef.current({
        invoiceComponentIndex,
        newInvoiceItemsForInvoiceComponent: [],
      });
      return;
    }

    // only set initial invoice items if none exists
    const initialInvoiceItems: InvoiceItemInputGQLType[] = _.compact(
      accrualsData.accounts.map((accountAccrual) => {
        // cannot pay down an admin agent principal payable.
        // this is a liability account that is used to track the principal amount that is owed to the admin agent
        // and is not borrower-facing.
        if (accountAccrual.tAccount.tAccount === 'adminAgentPrincipalPayable') {
          return null;
        }

        // cannot pay down long term debt if not all components are funded
        // can still pay down other tAccounts
        if (
          accountAccrual.tAccount.tAccount === 'longTermDebt' &&
          !areAllComponentsFundedOnLoan
        ) {
          return null;
        }

        const {
          id,
          tAccount,
          organizationId,
          creditFacilityId,
        } = accountAccrual.tAccount;

        return {
          accountingAccountId: id,
          amount: parseFloat(accountAccrual.totalAccruedAmount.toFixed(2)),
          status: 'created',
          accountingAccount: {
            id,
            tAccount,
            organizationId,
            creditFacilityId,
          },
        };
      }),
    );

    updateInvoiceComponentRef.current({
      invoiceComponentIndex,
      field: 'totalAmount',
      value: commonUtils.jsRound({
        value: _.sumBy(
          initialInvoiceItems,
          (invoiceItem) => invoiceItem.amount,
        ),
        precision: 2,
      }),
    });

    const newTotalForInvoiceComponent = commonUtils.jsRound({
      value: _.sumBy(initialInvoiceItems, (invoiceItem) => invoiceItem.amount),
      precision: 2,
    });
    updateInvoiceComponentRef.current({
      invoiceComponentIndex,
      field: 'totalAmount',
      value: newTotalForInvoiceComponent,
    });

    updateInvoiceItemsAndTotalsForInvoiceComponentRef.current({
      invoiceComponentIndex,
      newInvoiceItemsForInvoiceComponent: initialInvoiceItems,
    });

    const newTotalForInvoice = invoiceComponentsWithInvoiceItems.reduce<number>(
      (acc, invoiceComponent, idx) => {
        if (idx === invoiceComponentIndex) {
          return acc + newTotalForInvoiceComponent;
        }
        return acc + invoiceComponent.invoiceComponent.totalAmount;
      },
      0,
    );
    updateInvoiceRef.current({
      field: 'totalAmount',
      value: newTotalForInvoice,
    });
  }, [
    invoiceItems.length,
    accrualsData,
    isAccrualsLoading,
    loan.funding.requests,
    areAllComponentsFundedOnLoan,
    invoiceComponentIndex,
    invoiceComponentsWithInvoiceItems,
  ]);

  const accrualsByTAccountMap: AccrualsByTAccountMap = useMemo(() => {
    return (accrualsData?.accounts ?? []).reduce<AccrualsByTAccountMap>(
      (acc, account) => {
        // cannot pay down an admin agent principal payable.
        // this is a liability account that is used to track the principal amount that is owed to the admin agent
        // and is not borrower-facing.
        if (account.tAccount.tAccount === 'adminAgentPrincipalPayable') {
          return acc;
        }

        // cannot pay down long term debt if not all components are funded
        // can still pay down other tAccounts
        if (
          account.tAccount.tAccount === 'longTermDebt' &&
          !areAllComponentsFundedOnLoan
        ) {
          return acc;
        }

        const tAccountId = account.tAccount.id;

        return {
          ...acc,
          [tAccountId]: {
            tAccount: account.tAccount,
            projectedAccruedAmount:
              (acc[tAccountId]?.projectedAccruedAmount || 0) +
              account.projectedAccruedAmount,
            actualAccruedAmount:
              (acc[tAccountId]?.actualAccruedAmount || 0) +
              account.actualAccruedAmount,
            totalAccruedAmount:
              (acc[tAccountId]?.totalAccruedAmount || 0) +
              account.totalAccruedAmount,
          },
        };
      },
      {},
    );
  }, [accrualsData, areAllComponentsFundedOnLoan]);

  const canClickContinue =
    invoiceItems.length > 0 &&
    commonUtils.jsRound({
      value: _.sumBy(invoiceItems, ({ amount }) => amount),
      precision: 2,
    }) > 0;

  return (
    <Flex direction="column" h="100vh">
      {/* Header */}
      <Box mb="40px">
        <CreateInvoiceHeaderWithTimelineStepper completedStepIndex={2} />
      </Box>
      {invoice.invoiceType === 'loan' && loan && (
        <CreateInvoiceLineItemsCards />
      )}

      {/* Guided Form Step 3 */}
      <Flex direction="column" flexGrow={1}>
        <Box mb="40px" zIndex="dropdown">
          <Text fontWeight={600} mb="16px">
            Accrual Dates
          </Text>
          <FullDateRangePicker
            width="100%"
            onSelectDateRange={(newDateRange) => {
              const [newStartDate, newEndDate] = newDateRange;
              if (newStartDate && newEndDate) {
                updateInvoiceRef.current({
                  field: 'accrualStartDate',
                  value: commonDate.dateToFullDateString(newStartDate),
                });
                updateInvoiceRef.current({
                  field: 'accrualEndDate',
                  value: commonDate.dateToFullDateString(newEndDate),
                });
              }
            }}
            currentDateRange={{
              startDate: commonDate.fullDateStrToDate(invoice.accrualStartDate),
              endDate: commonDate.fullDateStrToDate(invoice.accrualEndDate),
            }}
            maxDate={
              commonDate.fullDateStrToDate(invoice.dueDate) >
              commonDate.fullDateStrToDate(loan.dates.maturityDate)
                ? commonDate.fullDateStrToDate(invoice.dueDate)
                : commonDate.fullDateStrToDate(loan.dates.maturityDate)
            }
            minDate={commonDate.fullDateStrToDate(loan.dates.originationDate)}
            showPreSelectedOptions={false}
          />
        </Box>

        <Box mb="24px">
          <CreateInvoiceLineItemsTable
            accrualsByTAccountMap={accrualsByTAccountMap}
            areAllComponentsFundedOnLoan={areAllComponentsFundedOnLoan}
            totalAccruedAndProjectedOutstandingBalanceAmount={
              totalAccruedAndProjectedOutstandingBalanceAmount
            }
            invoiceComponentIndex={invoiceComponentIndex}
          />
        </Box>
      </Flex>

      {/* Form Navigation */}
      <Flex justifyContent="space-between" my="40px">
        <Button variant="v2-ghost" onClick={() => setCurrentStepIndex(1)}>
          Back
        </Button>
        <Button
          variant="primary"
          colorScheme="v2-aurora"
          onClick={() => setCurrentStepIndex(3)}
          isDisabled={!canClickContinue}
        >
          Continue
        </Button>
      </Flex>
    </Flex>
  );
};

export const CreateInvoiceLineItems = ({
  setCurrentStepIndex,
  invoiceComponentIndex,
}: {
  setCurrentStepIndex: React.Dispatch<
    React.SetStateAction<CreateInvoiceStepIndex>
  >;
  invoiceComponentIndex: number;
}) => {
  const {
    selectedEntities: {
      loan: { loan },
    },
  } = useCreateInvoiceContext();

  // @dani todo: remove this once we implement support for facility-level invoices
  if (!loan) {
    return (
      <Flex direction="column" h="100vh">
        <Box mb="40px">
          <CreateInvoiceHeaderWithTimelineStepper completedStepIndex={2} />
        </Box>

        <Flex direction="column" flexGrow={1}>
          <InfoAlertWithDescription description="No loan selected. Please select a loan to continue." />
        </Flex>

        <Flex justifyContent="space-between" py="40px">
          <Button variant="v2-ghost" onClick={() => setCurrentStepIndex(1)}>
            Back
          </Button>
          <Button
            variant="primary"
            colorScheme="v2-aurora"
            onClick={() => setCurrentStepIndex(3)}
            isDisabled
          >
            Continue
          </Button>
        </Flex>
      </Flex>
    );
  }

  return (
    <CreateLoanInvoiceLineItems
      loan={loan}
      setCurrentStepIndex={setCurrentStepIndex}
      invoiceComponentIndex={invoiceComponentIndex}
    />
  );
};
